import React, { useEffect, useState } from "react";
import {
  TextField,
  Button,
  Box,
  Alert,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Typography,
} from "@mui/material";
import Parse from "parse";
import { useNavigate } from "react-router-dom";

Parse.initialize(
  process.env.REACT_APP_APP_ID,
  null,
  process.env.REACT_APP_MASTER_KEY
);
Parse.masterKey = process.env.REACT_APP_MASTER_KEY;
Parse.serverURL = process.env.REACT_APP_SERVER_URL;

const ClkkDialog = ({
  open,
  onClose,
  handleRefresh,
  amount,
  method,
  availableMethods,
  userId,
  sessionToken,
}) => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    notes: "",
    vendorId: "",
    amount: amount || "",
    payoutMethod: method || "",
    venmoPhone: "",
    paypalEmail: "",
    paypalPhone: "",
    cardEmail: "",
    cardPhone: "",
  });

  const [paypalOption, setPaypalOption] = useState("email");
  const [existingRecipientId, setExistingRecipientId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // ✅ On open, check if recipient already exists
  useEffect(() => {
    if (open && userId) {
      checkForExistingRecipient();
    }
  }, [open, userId]);

  const isValidEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const isValidPhone = (phone) => /^[0-9]{10}$/.test(phone);

  const checkForExistingRecipient = async () => {
    setLoading(true);
    try {
      const CLKK = Parse.Object.extend("CLKK");
      const query = new Parse.Query(CLKK);
      query.equalTo("userId", userId);
      query.descending("createdAt");
      query.limit(1);

      const existing = await query.first({ useMasterKey: true });
      if (existing) {
        const apiResponse = existing.get("apiResponse");
        const metadata = existing.get("metadata") || {};
        if (apiResponse?.recipientId) {
          setExistingRecipientId(apiResponse.recipientId);
          setForm((prev) => ({
            ...prev,
            name: existing.get("name") || "",
            notes: metadata.notes || "",
            vendorId: metadata.vendorId || "",
            venmoPhone: metadata.venmoPhone || "",
            paypalEmail: metadata.paypalEmail || "",
            paypalPhone: metadata.paypalPhone || "",
            cardEmail: metadata.cardEmail || "",
            cardPhone: metadata.cardPhone || "",
            payoutMethod: method || prev.payoutMethod,
          }));
        }
      }
    } catch (err) {
      setError(err.message || "Failed to fetch recipient");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handlePayout = async () => {
    setError("");
    if (!form.name) return setError("Name is required");

    // Validate amount
    if (!form.amount) return setError("Amount is required");
    const amountValue = parseFloat(form.amount);
    if (isNaN(amountValue) || amountValue < 25 || amountValue > 500) {
      return setError("Amount must be between $25 and $500");
    }

    // Validate method-specific fields
    if (form.payoutMethod === "venmo" && !isValidPhone(form.venmoPhone)) {
      return setError("Valid 10-digit Venmo Phone required");
    }
    if (form.payoutMethod === "paypal") {
      if (
        paypalOption === "email" &&
        (!isValidEmail(form.paypalEmail) || !form.paypalEmail)
      ) {
        return setError("Valid PayPal Email required");
      }
      if (
        paypalOption === "phone" &&
        (!isValidPhone(form.paypalPhone) || !form.paypalPhone)
      ) {
        return setError("Valid 10-digit PayPal Phone required");
      }
    }
    if (form.payoutMethod === "card") {
      if (!isValidEmail(form.cardEmail) || !form.cardEmail) {
        return setError("Valid Card Email required");
      }
      if (!isValidPhone(form.cardPhone) || !form.cardPhone) {
        return setError("Valid 10-digit Card Phone required");
      }
    }

    setLoading(true);
    try {
      const orderId = `ORDER-${Date.now()}`;
      const description = "cm.us";

      // Save recipient (new or update)
      const recipient = await Parse.Cloud.run(
        "saveClkkRecipient",
        {
          recipientId: existingRecipientId,
          userId,
          name: form.name,
          ...form,
        },
        { sessionToken: sessionToken }
      );

      // 🔑 Update recipientId if new one created
      if (recipient?.recipientId) {
        setExistingRecipientId(recipient.recipientId);
      }

      if (form.payoutMethod === "card") {
        const setupSession = await Parse.Cloud.run(
          "initiateClkkCardSetup",
          {
            recipientId: recipient?.recipientId || existingRecipientId,
            name: form.name,
            vendorId: form.vendorId,
            amount: amountValue,
            description,
            email: form.cardEmail,
            phone: form.cardPhone,
          },
          { sessionToken: sessionToken }
        );
        let sessionUrl = setupSession?.publicUrl;
        if (sessionUrl) {
          // sessionUrl = sessionUrl.replace(
          //   "https://pay.clkk-api.com",
          //   "https://pay.clkkapi.io"
          // );
            sessionUrl = sessionUrl.replace(
            "https://dev.pay.clkk-api.com",
            "https://pay-dev.clkkapi.io"
          );
          
          const von = {
            ...setupSession,
            publicUrl: sessionUrl,
          };
          navigate("/clk-card", { state: { response: von, recipientId:recipient?.recipientId || existingRecipientId,amount:amount ,sessionToken} });
          //window.open(sessionUrl, "_blank");
        }
      } else {
        await Parse.Cloud.run("createClkkPayout", {
          recipientId: recipient?.recipientId || existingRecipientId,
          method: form.payoutMethod,
          amount: amountValue,
          description,
          orderId,
          userId: userId,
        });
        setSuccess("Payout initiated successfully. You can close this window.");
        //onClose();
        //handleRefresh();
      }
    } catch (err) {
      setError(err.message || "Payout failed");
    } finally {
      setLoading(false);
    }
  };

  const renderPaymentInputs = () => {
    if (form.payoutMethod === "venmo") {
      return (
        <TextField
          label="Venmo Phone"
          name="venmoPhone"
          value={form.venmoPhone}
          onChange={(e) => {
            const val = e.target.value;
            if (/^[0-9]*$/.test(val) && val.length <= 10) {
              setForm((prev) => ({ ...prev, venmoPhone: val }));
            }
          }}
          inputProps={{ maxLength: 10 }}
          fullWidth
        />
      );
    }
    if (form.payoutMethod === "paypal") {
      return (
        <>
          <FormControl fullWidth>
            <InputLabel id="paypal-contact-type-label">
              PayPal Contact Type
            </InputLabel>
            <Select
              labelId="paypal-contact-type-label"
              id="paypal-contact-type"
              value={paypalOption}
              onChange={(e) => setPaypalOption(e.target.value)}
            >
              <MenuItem value="email">Email</MenuItem>
              <MenuItem value="phone">Phone</MenuItem>
            </Select>
          </FormControl>
          {paypalOption === "email" ? (
            <TextField
              label="PayPal Email"
              name="paypalEmail"
              value={form.paypalEmail}
              onChange={handleChange}
              fullWidth
            />
          ) : (
            <TextField
              label="PayPal Phone"
              name="paypalPhone"
              value={form.paypalPhone}
              onChange={(e) => {
                const val = e.target.value;
                if (/^[0-9]*$/.test(val) && val.length <= 10) {
                  setForm((prev) => ({ ...prev, paypalPhone: val }));
                }
              }}
              inputProps={{ maxLength: 10 }}
              fullWidth
            />
          )}
        </>
      );
    }
    if (form.payoutMethod === "card") {
      return (
        <>
          <TextField
            label="Card Email"
            name="cardEmail"
            value={form.cardEmail}
            onChange={handleChange}
            fullWidth
          />
          <TextField
            label="Card Phone"
            name="cardPhone"
            value={form.cardPhone}
            onChange={(e) => {
              const val = e.target.value;
              if (/^[0-9]*$/.test(val) && val.length <= 10) {
                setForm((prev) => ({ ...prev, cardPhone: val }));
              }
            }}
            inputProps={{ maxLength: 10 }}
            fullWidth
          />
        </>
      );
    }
    return null;
  };

  return (
    <Box sx={{ p: 3, border: "1px solid #ddd", borderRadius: 2 }}>
      <Typography variant="h6" gutterBottom>
        Process Payout
      </Typography>

      {success && (
        <Alert
          style={{ marginBottom: "20px" }}
          severity="success"
          onClose={() => setSuccess("")}
        >
          {success}
        </Alert>
      )}
      {error && (
        <Alert
          severity="error"
          style={{ marginBottom: "20px" }}
          onClose={() => setError("")}
        >
          {error}
        </Alert>
      )}
      {!success && (
        <>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel id="payout-method-label">Choose Method</InputLabel>
              <Select
                labelId="payout-method-label"
                name="payoutMethod"
                value={form.payoutMethod}
                onChange={handleChange}
              >
                {availableMethods?.map((m) => (
                  <MenuItem key={m} value={m}>
                    {m.toUpperCase()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Name"
              name="name"
              value={form.name}
              onChange={handleChange}
              fullWidth
            />
            {renderPaymentInputs()}
            <TextField
              label="Amount"
              name="amount"
              value={form.amount}
              onChange={handleChange}
              fullWidth
              helperText="Enter between $25 and $500"
            />

            {error && <Alert severity="error">{error}</Alert>}
          </Box>

          <Box
            sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 2 }}
          >
            <Button
              onClick={onClose}
              disabled={loading}
              variant="outlined"
              style={{
                outline: "1px solid #FFC107",
                color: "white",
                textTransform: "none",
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handlePayout}
              disabled={loading || !form.payoutMethod}
              style={{
                background: "#FFC107",
                color: "#0A0A0A",
                textTransform: "none",
                "&:hover": { backgroundColor: "#FFA000" },
              }}
            >
              {loading ? "Processing..." : "Confirm"}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default ClkkDialog;
